import { useState, type ChangeEvent } from 'react';
import { IconButton, InputAdornment } from '@mui/material';
import { FloatingLabelInputLayout } from 'framework/ui/layout';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import { clamp } from 'lodash';
import type { FillInputProps } from '../fillInput';
import { numberOf } from '../shared/bounds';
import { sameValue, stepped, textOfValue, valueOfText } from '../shared/numberText';
import type { NumberStepperSettings } from './settings';

type StepperValue = number | string | null;

type StepButtonProps = {
  direction: 1 | -1;
  disabled: boolean;
  onPress: () => void;
};

const StepButton = ({ direction, disabled, onPress }: StepButtonProps) => {
  const increase = direction > 0;
  const position = increase ? 'end' : 'start';
  return (
    <InputAdornment position={position}>
      <IconButton aria-label={increase ? 'Increase' : 'Decrease'} edge={position} disabled={disabled} onClick={onPress}>
        {increase ? <Add /> : <Remove />}
      </IconButton>
    </InputAdornment>
  );
};

export const NumberStepperInput = (props: FillInputProps<StepperValue, NumberStepperSettings>) => {
  const {
    settings,
    inputId,
    label,
    showLabel,
    describedBy,
    value,
    onChange,
    onBlur,
    disabled,
    required,
    error,
    bounds,
  } = props;
  const step = settings.step ?? 1;

  const [typed, setTyped] = useState<string | null>(null);
  const text = typed !== null && sameValue(valueOfText(typed), value) ? typed : textOfValue(value);

  const min = numberOf(bounds.min);
  const max = numberOf(bounds.max);
  const current = typeof value === 'number' && Number.isFinite(value) ? value : null;
  const canDecrease = !disabled && !(current !== null && min !== undefined && current <= min);
  const canIncrease = !disabled && !(current !== null && max !== undefined && current >= max);

  const move = (direction: 1 | -1) => {
    const from = current === null ? (min ?? (direction > 0 ? step : 0)) : stepped(current, step, direction);
    const next = clamp(from, min ?? -Infinity, max ?? Infinity);
    setTyped(null);
    onChange(next);
  };

  const type = (next: string) => {
    setTyped(next);
    onChange(valueOfText(next));
  };

  return (
    <FloatingLabelInputLayout
      source={inputId}
      label={showLabel ? label : undefined}
      value={text}
      disabled={disabled}
      isRequired={required}
      errorMessages={error ? [''] : []}
      sx={{ mb: 0 }}
      slotProps={{
        input: {
          startAdornment: <StepButton direction={-1} disabled={!canDecrease} onPress={() => move(-1)} />,
          endAdornment: <StepButton direction={1} disabled={!canIncrease} onPress={() => move(1)} />,
        },
        htmlInput: {
          onChange: (event: ChangeEvent<HTMLInputElement>) => type(event.currentTarget.value),
          onBlur,
          inputMode: 'decimal',
          autoComplete: 'off',
          'aria-describedby': describedBy,
          'aria-required': required || undefined,
        },
      }}
    />
  );
};
